import { useState, type ReactNode } from 'react';
import {
  Box, Typography, Stack, CircularProgress, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useProjectInitialization,
  type ProjectInitialization,
} from '../../../api/hooks/useProjectInitialization';
import { useUnsicherheiten, type Unsicherheiten } from '../../../api/hooks/useUnsicherheiten';
import { useProject } from '../../../api/hooks/useProjects';
import { useUpsertReifegrad, computeMaturityFromForm } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { ProjektCharakterSections } from './ProjektCharakter';
import { buildZieleReport } from '../reports/zieleReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';

/**
 * Schritt 4 — Ziele. Live-Speichern (Autosave). "Bekannte Unbekannte / Risiken"
 * liest/schreibt das bestehende uncertainties.bekannte_unbekannte (eigene
 * Tabelle, kein Datenverlust); die uebrigen Unsicherheiten-Felder bleiben
 * unberuehrt.
 */
export function ZieleTab({ projectId, embedded = false }: { projectId: string; embedded?: boolean }) {
  const { isLoading, data, save } = useProjectInitialization(projectId);
  const { isLoading: uncLoading, data: uncData, save: saveUnc } = useUnsicherheiten(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);
  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  if (isLoading || uncLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pdfMeta: ReportMeta = { projectName, pageTitle: 'Ziele' };
  const buildReport = () =>
    buildZieleReport({
      zieleHaupt: data.ziele_haupt ?? '',
      zieleTeil: data.ziele_teil ?? '',
      zieleNicht: data.ziele_nicht ?? '',
      zieleAkzeptanz: data.ziele_akzeptanz ?? '',
      bekannteUnbekannte: uncData.bekannte_unbekannte ?? '',
    });

  return (
    <ZieleForm
      data={data}
      bekannteUnbekannte={uncData.bekannte_unbekannte}
      save={save}
      saveUnc={saveUnc}
      showCharakter={!embedded}
      projectId={projectId}
      headerAction={embedded ? null : <PdfExportButton meta={pdfMeta} build={buildReport} />}
      onReifegradUpdate={(level) =>
        upsertReifegrad.mutateAsync({ schritt: 4, data: { reifegrad: level } }).catch(() => undefined)
      }
    />
  );
}

interface ZieleFormValues {
  /** Zweite, synchrone Darstellung der Problemstellung aus der Machbarkeit
   *  (gleiches initialization-Feld). An erster Position der Ziele. */
  machbarkeit_problem: string;
  ziele_haupt: string;
  ziele_teil: string;
  ziele_nicht: string;
  ziele_akzeptanz: string;
  bekannte_unbekannte: string;
}

interface ZieleFormProps {
  data: ProjectInitialization;
  bekannteUnbekannte: string;
  save: (patch: Partial<ProjectInitialization>) => Promise<void>;
  saveUnc: (patch: Partial<Unsicherheiten>) => Promise<void>;
  onReifegradUpdate: (level: number) => Promise<unknown>;
  headerAction?: ReactNode;
  /** Vertragstyp, Dreieck und Stacey unter den Zielen zeigen (PHQ-78). Im Projektauftrag stehen sie schon weiter unten. */
  showCharakter?: boolean;
  projectId?: string;
}

function ZieleForm({ data, bekannteUnbekannte, save, saveUnc, onReifegradUpdate, headerAction, showCharakter = false, projectId }: ZieleFormProps) {
  const { t } = useTranslation('projects');

  const [form, setForm] = useState<ZieleFormValues>({
    machbarkeit_problem: data.machbarkeit_problem ?? '',
    ziele_haupt: data.ziele_haupt ?? '',
    ziele_teil: data.ziele_teil ?? '',
    ziele_nicht: data.ziele_nicht ?? '',
    ziele_akzeptanz: data.ziele_akzeptanz ?? '',
    bekannte_unbekannte: bekannteUnbekannte ?? '',
  });

  const autosave = useAutosave<ZieleFormValues>({
    // Auch speichern, wenn (nur) die Problemstellung gepflegt wird — sonst ginge
    // eine reine Problemstellungs-Änderung bei noch leerem Hauptziel verloren.
    canSave: (f) => f.ziele_haupt.trim().length > 0 || f.machbarkeit_problem.trim().length > 0,
    onSave: async (f) => {
      await save({
        machbarkeit_problem: f.machbarkeit_problem.trim(),
        ziele_haupt: f.ziele_haupt.trim(),
        ziele_teil: f.ziele_teil,
        ziele_nicht: f.ziele_nicht,
        ziele_akzeptanz: f.ziele_akzeptanz,
      });
      // Nur das eine Feld der Unsicherheiten-Tabelle patchen.
      await saveUnc({ bekannte_unbekannte: f.bekannte_unbekannte });
      const filled = [
        f.ziele_haupt.trim().length > 0,
        f.ziele_teil.trim().length > 0,
        f.ziele_nicht.trim().length > 0,
        f.ziele_akzeptanz.trim().length > 0,
        f.bekannte_unbekannte.trim().length > 0,
      ];
      const level = computeMaturityFromForm(filled, [true, false, false, false, false]);
      await onReifegradUpdate(level);
    },
  });

  const update = (patch: Partial<ZieleFormValues>) => {
    const next = { ...form, ...patch };
    setForm(next);
    autosave.schedule(next);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('ziele.title', 'Ziele')}
          </Typography>
          <Typography color="text.secondary">
            {t('ziele.description', 'Schritt 4 — Hauptziel, Meilensteine, Nicht-Ziele, Akzeptanzkriterien und Risiken.')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {headerAction}
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
        </Stack>
      </Stack>

      <PmCard>
        <Stack spacing={2.5}>
          <TextField
            label={t('machbarkeit.problem', 'Vision')}
            helperText={t('ziele.problemHelper', 'Warum existiert das Projekt?')}
            value={form.machbarkeit_problem}
            onChange={(e) => update({ machbarkeit_problem: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            required
            label={t('ziele.haupt', 'Hauptziel')}
            helperText={t('ziele.hauptHelper', 'Zentrale Projektzielsetzung — was soll erreicht werden?')}
            value={form.ziele_haupt}
            onChange={(e) => update({ ziele_haupt: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label={t('ziele.teil', 'Meilensteine')}
            helperText={t('ziele.teilHelper', 'Ein Meilenstein pro Zeile')}
            value={form.ziele_teil}
            onChange={(e) => update({ ziele_teil: e.target.value })}
            multiline
            rows={4}
            fullWidth
          />
          <TextField
            label={t('ziele.nicht', 'Nicht-Ziele')}
            helperText={t('ziele.nichtHelper', 'Was wird ausdrücklich NICHT erreicht?')}
            value={form.ziele_nicht}
            onChange={(e) => update({ ziele_nicht: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label={t('ziele.akzeptanz', 'Akzeptanzkriterien')}
            helperText={t('ziele.akzeptanzHelper', 'Wann ist das Projekt ein Erfolg?')}
            value={form.ziele_akzeptanz}
            onChange={(e) => update({ ziele_akzeptanz: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label={t('ziele.bekannteUnbekannte', 'Bekannte Unbekannte / Risiken')}
            helperText={t('ziele.bekannteUnbekannteHelper', 'Offene Fragen und bekannte Unsicherheiten, die früh geklärt werden müssen')}
            value={form.bekannte_unbekannte}
            onChange={(e) => update({ bekannte_unbekannte: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </PmCard>

      {showCharakter && projectId && <ProjektCharakterSections projectId={projectId} />}
    </Box>
  );
}
